import { useRef } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { BillTipSnapshot, CloseBillTipIn } from "../../../../../api/bills";
import type { ActivePaymentMethod } from "../../../../../api/paymentMethods";
import { useTipsLocked } from "../../../../../hooks/useEdgeMode";
import {
  fetchFreshTipSettingsQuery,
  useTipSettings,
} from "../../../../../hooks/useTipSettings";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";
import {
  billMethodCarriesTip,
  buildTipRequest,
  CARD_PAYMENT_METHOD_ID,
  recordedTipIssue,
  tipMethodRefusal,
  validateTip,
  type TipMethodContext,
} from "./tipMath";
import { useTipIssueText } from "./tipIssueText";
import { useTipEntry } from "./useTipEntry";

/** Как изглежда секцията: няма я (изключено/повторен опит), заключена (без
 *  интернет към каса без бакшиш; сметка без нито един ред — BE отказва
 *  `tip_requires_items`) или за въвеждане. */
export type TipSectionMode = "none" | "locked" | "noItems" | "entry";

/** Бакшишът, записан по сметката (за повторния опит — само за четене).
 *  Идва от `BillOut` (`GET /bills`) или от реда в историята (`GET
 *  /bills/history` — „Фискализирай" от „Възстановяване"); от снимката трябва
 *  само името на метода (изтрит метод). */
export interface RecordedBillTip {
  // Документът на сметката (`GET /bills`) праща сумата като JSON число, като
  // `total_amount`; историята — като низ.
  tip_amount?: number | string;
  tip_payment_method_id?: number | null;
  tip_snapshot?: Pick<BillTipSnapshot, "payment_method_name_bg"> | null;
}

interface UseBillCloseTipArgs
  extends Omit<TipMethodContext, "activeMethods" | "billMethodId"> {
  open: boolean;
  /** „Закрий" на закрита без бон — бакшишът не се въвежда, само се показва. */
  isRetry: boolean;
  /** Поръчаното (`bill.total_amount` / сумата на избраните редове). */
  orderedAmount: string | number;
  activeMethods: ActivePaymentMethod[];
  methodNameOf: (methodId: number) => string;
  billMethodId: number | null;
  recordedTip?: RecordedBillTip | null;
  /** Сметката има поне един ред (`billHasActiveRows`) — без редове бакшиш не
   *  може (BE `tip_requires_items`); непознато → true. */
  hasActiveRows?: boolean;
  /** Сметка с отстъпка в € на цялата сметка — без бакшиш, секцията я няма
   *  (BE го отказва: апаратът разнася отстъпката и по реда на бакшиша). */
  hasBillDiscount?: boolean;
  onTipDropped: () => void;
}

export type TipCloseOutcome =
  | { ok: true; tip?: CloseBillTipIn }
  | { ok: false; warning: string };

/**
 * Бакшишът на модал за плащане („Закрий", „Раздели и плати"): настройките,
 * въведеното и проверките преди затварянето. Бакшишът се плаща с метода на
 * сметката и картата се показва само при метод, избран в настройките („В
 * брой" и „Смесено" — никога). Модалът вика само
 * `checkBeforeClose()` (веднага, без заявка) и `prepareForClose()` (след
 * свежото `GET /bills`); без бакшиш второто връща `{ ok: true }` без `tip` и
 * тялото на заявката остава старото.
 */
export function useBillCloseTip({
  open,
  isRetry,
  orderedAmount,
  activeMethods,
  methodNameOf,
  billMethodId,
  recordedTip = null,
  hasActiveRows = true,
  hasBillDiscount = false,
  onTipDropped,
  ...permissions
}: UseBillCloseTipArgs) {
  const { t } = useTranslation();
  const issueText = useTipIssueText();
  const { data: settings } = useTipSettings();
  const tipsLocked = useTipsLocked();

  const tipsEnabled = !isRetry && !hasBillDiscount && settings?.enabled === true;
  const sectionMode: TipSectionMode = !tipsEnabled
    ? "none"
    : tipsLocked
      ? "locked"
      : !hasActiveRows
        ? "noItems"
        : "entry";

  const methodContext: TipMethodContext = {
    ...permissions,
    activeMethods,
    billMethodId,
  };
  // Картата „Бакшиш" — само при метод на сметката, избран в настройките;
  // иначе я няма (модалът остава с подредбата за бакшиш, без да подскача).
  const carriesTip =
    settings != null && billMethodCarriesTip(billMethodId, settings.paymentMethodIds);
  const cardMode: TipSectionMode = carriesTip ? sectionMode : "none";
  const tipMethodId = carriesTip ? billMethodId : null;
  // Методът е в настройките, но бакшишът с него не може да влезе в бона
  // (няма слот на фискалното устройство) — казва се под сумата.
  const methodRefusal =
    cardMode === "entry" && settings && tipMethodId !== null
      ? tipMethodRefusal(
          tipMethodId,
          settings.paymentMethodIds,
          methodContext,
          methodNameOf(tipMethodId),
        )
      : null;

  // Методът, при който е въведен бакшишът: смени ли човекът сам метода на
  // сметката, картата просто изчезва; отпадне ли бакшишът по друга причина
  // (без интернет, сменени настройки) — казва се.
  const enteredWithMethodRef = useRef(billMethodId);
  if (cardMode === "entry") enteredWithMethodRef.current = billMethodId;

  const tipEntry = useTipEntry({
    open,
    active: open && cardMode === "entry",
    orderedAmount,
    settings: settings ?? null,
    tipMethodId,
    methodRefusal,
    onTipDropped: () => {
      if (enteredWithMethodRef.current === billMethodId) onTipDropped();
    },
  });


  // Повторен опит: записаният бакшиш се показва и НЕ се праща (BE пази
  // своя). Методът му трябва да може да мине по пътя на НОВИЯ избор.
  const recordedTipCents = isRetry
    ? (parseMoneyToCents(recordedTip?.tip_amount) ?? 0)
    : 0;
  const recordedMethodId = recordedTip?.tip_payment_method_id ?? null;
  const recorded =
    recordedTipCents > 0
      ? {
          amount: formatCents(recordedTipCents),
          /** За дължимото (`useTipDueSummary`): със същия метод като
           *  сметката записаният бакшиш влиза в „Общо". */
          cents: recordedTipCents,
          methodId: recordedMethodId,
          methodName:
            recordedMethodId !== null
              ? methodNameOf(recordedMethodId) ||
                (recordedTip?.tip_snapshot?.payment_method_name_bg ?? "")
              : (recordedTip?.tip_snapshot?.payment_method_name_bg ?? ""),
        }
      : null;

  // Повторен опит: записаният бакшиш срещу НОВИЯ избор — изтрит метод,
  // правото за метода му, пътят (BE `check_retry_tip`).
  const recordedIssue = () =>
    recorded
      ? recordedTipIssue({
          methodId: recordedMethodId,
          methodName: recorded.methodName,
          billMethodId,
          activeMethods,
          canPaymentCash: permissions.canPaymentCash,
          canPaymentCard: permissions.canPaymentCard,
          sendsNonFiscalTotal: permissions.sendsNonFiscalTotal,
        })
      : null;

  const orderedChangedText = (fromCents: number, toCents: number) =>
    t("closeTable.tip.errors.bill_total_changed", {
      old: formatCents(fromCents),
      new: formatCents(toCents),
    });

  /** Веднага при „Закриване"/Enter — без заявка. Текст = не закривай. */
  const checkBeforeClose = (): string | null => {
    // Сметка без редове (`tip_requires_items`): секцията е заключена
    // (`noItems`) и бакшиш изобщо не може да се въведе; свежата сметка се
    // сверява наново в `prepareForClose`.
    if (tipEntry.active) {
      if (tipEntry.orderedChange) {
        return orderedChangedText(
          tipEntry.orderedChange.fromCents,
          tipEntry.orderedChange.toCents,
        );
      }
      if (tipEntry.issue) return issueText(tipEntry.issue);
    }
    const issue = recordedIssue();
    return issue ? issueText(issue) : null;
  };

  /**
   * След свежото `GET /bills`: прегледаното поръчано още ли е същото, има ли
   * свежата сметка редове и минава ли бакшишът по СВЕЖИТЕ настройки (друг ги
   * е сменил, докато модалът е отворен). Само при въведен бакшиш се тегли и
   * свежото `GET /payment-methods/tip-settings`. Отказът за метода е с кода
   * на BE (403 за правото, пътят), не общото „не е разрешен".
   */
  const prepareForClose = async (
    queryClient: QueryClient,
    freshOrderedCents: number,
    options?: { includeTotalWithTip?: boolean; hasActiveRows?: boolean },
  ): Promise<TipCloseOutcome> => {
    if (!tipEntry.active) return { ok: true };
    if (tipEntry.isStaleAgainst(freshOrderedCents)) {
      return {
        ok: false,
        warning: orderedChangedText(
          tipEntry.reviewedOrderedCents ?? tipEntry.orderedCents,
          freshOrderedCents,
        ),
      };
    }
    const tipCents = tipEntry.tipCents;
    if (tipCents === 0) return { ok: true };
    if (tipCents < 0) return { ok: false, warning: issueText({ code: "tip_negative" }) };

    const freshSettings = await queryClient.fetchQuery(fetchFreshTipSettingsQuery);
    if (!freshSettings?.enabled) {
      return { ok: false, warning: issueText({ code: "tip_disabled" }) };
    }
    const tipMethodId = tipEntry.tipMethodId;
    const freshRefusal =
      tipMethodId === null
        ? null
        : tipMethodRefusal(
            tipMethodId,
            freshSettings.paymentMethodIds,
            methodContext,
            methodNameOf(tipMethodId),
          );
    const issue = validateTip({
      tipCents,
      settings: freshSettings,
      tipMethodId,
      allowedMethodIds: tipMethodId !== null && freshRefusal === null ? [tipMethodId] : [],
      confirmedTipCents: tipEntry.isConfirmed ? tipCents : null,
      methodRefusal: freshRefusal,
      hasActiveRows: options?.hasActiveRows,
    });
    if (issue) return { ok: false, warning: issueText(issue) };
    return {
      ok: true,
      tip: buildTipRequest({
        tipCents,
        tipMethodId: tipEntry.tipMethodId,
        reviewedOrderedCents: freshOrderedCents,
        settings: freshSettings,
        includeTotalWithTip: options?.includeTotalWithTip,
      }),
    };
  };

  return {
    settings: settings ?? null,
    /** Подредбата на модала: бакшишът е включен (не зависи от метода). */
    sectionMode,
    /** Картата „Бакшиш" при текущия метод на сметката. */
    cardMode,
    tipEntry,
    recorded,
    /** Записаният бакшиш (повторен опит) е с карта — терминалът може да чака
     *  човек, макар тялото да не го носи (`closeBillRetry` с картовия бюджет). */
    recordedTipWaitsForCard:
      recorded !== null && recordedMethodId === CARD_PAYMENT_METHOD_ID,
    /** Бутонът „Закриване" чака: грешка в бакшиша или неприет нов преглед. */
    blocksClose: tipEntry.active && tipEntry.blocksClose,
    checkBeforeClose,
    prepareForClose,
  };
}

export type BillCloseTip = ReturnType<typeof useBillCloseTip>;
